// useIdentityVerification — single source of truth for the Layer 0
// identity-gate state machine on the frontend.
//
// Talks to the 6 backend endpoints exposed in app.py. Exposes a clean
// imperative API so the wizard UI doesn't have to know about REST shape:
//
//   const { status, config, uploadId, uploadSelfie, verify, runLiveness, refresh } = useIdentityVerification(testId)
//
// Status lifecycle (server-authoritative):
//   'loading'                       — initial fetch in flight
//   'awaiting_id'                   — no ID photo yet
//   'awaiting_selfie'               — ID uploaded, no selfie yet
//   'awaiting_verify'               — both images uploaded, no verdict yet
//   'pending' | 'manual_review' | 'passed' | 'failed' | 'skipped'
//                                   — server verdict values, end states
//
// All network calls return { ok, ...data, error? } so the caller can
// render error UI without try/catch.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const ENDPOINTS = {
  config:           '/api/identity/config',
  status:           (testId) => `/api/identity/status/${encodeURIComponent(testId)}`,
  uploadId:         '/api/identity/upload-id',
  uploadSelfie:     '/api/identity/upload-selfie',
  verify:           '/api/identity/verify',
  retry:            '/api/identity/retry',
  livenessSession:  '/api/identity/liveness/session',
  livenessComplete: '/api/identity/liveness/complete',
};


/**
 * Derive a wizard-friendly status string from a server row + config.
 * Server `verdict` is authoritative for end states; for in-progress
 * flows we map row presence/absence into the right step.
 */
function deriveStatus(row, config) {
  if (!row) return 'awaiting_id';
  const v = row.verdict;
  // End states — show them as-is.
  if (v === 'passed' || v === 'failed' || v === 'manual_review' || v === 'skipped') {
    return v;
  }
  // In-progress: figure out which step the candidate is on.
  if (!row.id_photo_s3_key) return 'awaiting_id';
  if (!row.selfie_s3_key) return 'awaiting_selfie';
  return 'awaiting_verify';
}


export function useIdentityVerification(testId, {
  applicationId = null,
  candidateEmail = null,
} = {}) {
  const [config, setConfig] = useState(null);   // {id_upload, liveness_check, face_match, ...}
  const [row, setRow] = useState(null);         // server row, or null
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // ── Fetch config + current row on mount ─────────────────────────────────
  const refresh = useCallback(async () => {
    if (!testId) return;
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(ENDPOINTS.config),
        axios.get(ENDPOINTS.status(testId)),
      ]);
      setConfig(configRes.data);

      // If NONE of the identity sub-features are enabled, the gate is a
      // no-op — surface 'skipped' so the wizard renders nothing and the
      // test starts immediately. This is the right behaviour for tenants
      // who haven't turned identity verification on.
      const anyEnabled =
        configRes.data.id_upload ||
        configRes.data.face_match ||
        configRes.data.liveness_check;
      if (!anyEnabled) {
        setStatus('skipped');
        setRow(null);
        return;
      }

      const nextRow = statusRes.data?.exists ? statusRes.data : null;
      setRow(nextRow);
      setStatus(deriveStatus(nextRow, configRes.data));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[identity] refresh failed:', err?.message || err);
      setError(err?.message || 'Failed to load verification status');
      setStatus('error');
    }
  }, [testId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Upload ID document (Blob or File) ──────────────────────────────────
  const uploadId = useCallback(async (imageBlob) => {
    if (!testId || !imageBlob) return { ok: false, error: 'missing data' };
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('test_id', testId);
      fd.append('image', imageBlob, 'id.jpg');
      if (applicationId !== null && applicationId !== undefined) {
        fd.append('application_id', String(applicationId));
      }
      if (candidateEmail) fd.append('candidate_email', candidateEmail);

      const res = await axios.post(ENDPOINTS.uploadId, fd, {
        // axios sets multipart boundary correctly when value is FormData
        timeout: 30000,
      });
      // refresh() pulls fresh server state — single source of truth.
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      // Backend returns a 400 with `user_message` when the uploaded image
      // doesn't look like an ID document (selfie-as-ID bypass guard).
      // Prefer that friendly text over the raw error code so the wizard
      // can display something the candidate can actually act on.
      const data = err?.response?.data || {};
      const msg = data.user_message || data.error || err?.message || 'upload failed';
      setError(msg);
      return {
        ok: false,
        error: msg,
        error_code: data.error,
        validation: data.validation,
      };
    } finally {
      setBusy(false);
    }
  }, [testId, applicationId, candidateEmail, refresh]);

  // ── Upload selfie ───────────────────────────────────────────────────────
  const uploadSelfie = useCallback(async (imageBlob) => {
    if (!testId || !imageBlob) return { ok: false, error: 'missing data' };
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('test_id', testId);
      fd.append('image', imageBlob, 'selfie.jpg');

      const res = await axios.post(ENDPOINTS.uploadSelfie, fd, {
        timeout: 30000,
      });
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'upload failed';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [testId, refresh]);

  // ── Run CompareFaces and set the verdict ───────────────────────────────
  const verify = useCallback(async () => {
    if (!testId) return { ok: false, error: 'missing test_id' };
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(ENDPOINTS.verify, { test_id: testId }, {
        timeout: 30000,
      });
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'verify failed';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [testId, refresh]);

  // ── Retry from the selfie step ─────────────────────────────────────────
  // Used by the failed/manual_review states. Server clears the selfie +
  // match score + verdict so the wizard re-renders the selfie step.
  // ID photo is preserved — usually the ID was fine, just the selfie
  // was bad (lighting, angle, glasses).
  const retry = useCallback(async () => {
    if (!testId) return { ok: false, error: 'missing test_id' };
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(ENDPOINTS.retry, { test_id: testId }, {
        timeout: 10000,
      });
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'retry failed';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [testId, refresh]);

  // ── Liveness (optional). Kept here so the wizard can opt-in. ───────────
  const startLivenessSession = useCallback(async () => {
    if (!testId) return { ok: false, error: 'missing test_id' };
    try {
      const res = await axios.post(ENDPOINTS.livenessSession,
        { test_id: testId }, { timeout: 15000 });
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'liveness failed';
      return { ok: false, error: msg };
    }
  }, [testId]);

  const completeLivenessSession = useCallback(async (sessionId) => {
    if (!testId || !sessionId) {
      return { ok: false, error: 'missing test_id or session_id' };
    }
    try {
      const res = await axios.post(ENDPOINTS.livenessComplete,
        { test_id: testId, session_id: sessionId },
        { timeout: 15000 });
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'liveness fetch failed';
      return { ok: false, error: msg };
    }
  }, [testId, refresh]);

  return {
    config,
    row,
    status,
    error,
    busy,
    refresh,
    uploadId,
    uploadSelfie,
    verify,
    retry,
    startLivenessSession,
    completeLivenessSession,
  };
}
